import { useEffect, useRef } from 'react'
import { getAuth, onAuthChange, refreshAccessToken, setAuth, registerRefreshHandler } from '../lib/auth.js'

// Schedules a refresh ~60s before tokenExpiry. Single-flight; while one
// refresh is in flight, additional callers wait on the same promise.
//
// Failure policy:
//   - First failure: schedule a retry in 30s.
//   - Second consecutive failure: dispatch `next-session-expired` so the
//     SessionExpiredModal renders.
//   - AS reply with `isTokenExpired` (400/401 on the refresh grant): skip
//     retries and fire the modal immediately — the refresh token is dead.
//
// fetchWithAuth wires registerRefreshHandler() to this so a 401 mid-call
// triggers one refresh + retry without re-implementing the lock.
//
// Adapted from Workbench origin/main@60e779e:src/hooks/useTokenRefresh.js;
// simplified (no visibilitychange handler, fewer retries) for v0.9.0.

const PROACTIVE_LEAD_MS = 60_000
const RETRY_AFTER_MS = 30_000
const SESSION_EXPIRED_EVENT = 'next-session-expired'

let inFlight = null

function runRefreshOnce() {
  if (inFlight) return inFlight
  inFlight = (async () => {
    try {
      const tokens = await refreshAccessToken()
      setAuth({
        accessToken: tokens.accessToken,
        refreshToken: tokens.refreshToken,
        tokenExpiry: tokens.expiresIn ? Date.now() + tokens.expiresIn * 1000 : null,
      })
      return true
    } finally {
      inFlight = null
    }
  })()
  return inFlight
}

export function dispatchSessionExpired(reason) {
  if (typeof window === 'undefined') return
  window.dispatchEvent(new window.CustomEvent(SESSION_EXPIRED_EVENT, { detail: { reason } }))
}

export function onSessionExpired(handler) {
  if (typeof window === 'undefined') return () => {}
  window.addEventListener(SESSION_EXPIRED_EVENT, handler)
  return () => window.removeEventListener(SESSION_EXPIRED_EVENT, handler)
}

export default function useTokenRefresh() {
  const failuresRef = useRef(0)

  useEffect(() => {
    // Wire fetchWithAuth's 401 retry to the same single-flight refresh.
    registerRefreshHandler(async () => {
      try {
        await runRefreshOnce()
        return true
      } catch (err) {
        if (err?.isTokenExpired) dispatchSessionExpired('refresh-token-expired')
        return false
      }
    })
    return () => registerRefreshHandler(null)
  }, [])

  useEffect(() => {
    let cancelled = false
    let timerId = null

    const schedule = () => {
      if (cancelled) return
      const { accessToken, tokenExpiry } = getAuth()
      if (!accessToken || !tokenExpiry) return

      const lead = tokenExpiry - Date.now() - PROACTIVE_LEAD_MS
      const delay = Math.max(0, lead)

      timerId = setTimeout(async () => {
        try {
          await runRefreshOnce()
          failuresRef.current = 0
        } catch (err) {
          if (err?.isTokenExpired) {
            dispatchSessionExpired('refresh-token-expired')
            return
          }
          failuresRef.current += 1
          if (failuresRef.current >= 2) {
            dispatchSessionExpired('refresh-failed-twice')
            return
          }
          console.warn('[useTokenRefresh] refresh failed, retrying in 30s:', err?.message)
          timerId = setTimeout(schedule, RETRY_AFTER_MS)
        }
      }, delay)
    }

    schedule()
    const off = onAuthChange(() => {
      clearTimeout(timerId)
      failuresRef.current = 0
      schedule()
    })

    return () => {
      cancelled = true
      clearTimeout(timerId)
      off()
    }
  }, [])
}
